import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css'; // Import quill styles
import { 
  PaperAirplaneIcon, 
  XMarkIcon, 
  PaperClipIcon,
  TrashIcon,
  DocumentIcon,
  ClockIcon,
  SparklesIcon,
  ArrowUturnLeftIcon,
  BookmarkIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline';
import { mailApi } from '../../api/mailApi';
import toast from 'react-hot-toast';

// Default built-in templates
const defaultTemplates = [
  {
    id: 'payment_reminder',
    name: 'Payment Reminder',
    subject: 'Outstanding Payment Reminder - [Business_Name]',
    body: '<p>Dear [Customer_Name],</p><p>This is a friendly reminder that a payment of <strong>[Due_Amount]</strong> is currently due.</p><p>Please arrange for the payment at your earliest convenience.</p><p>Thank you,<br/>[Business_Name]</p>'
  },
  {
    id: 'festive_greeting',
    name: 'Festive Greeting',
    subject: 'Happy Diwali from [Business_Name]!',
    body: '<p>Dear [Customer_Name],</p><p>Wishing you and your family a very Happy Diwali!</p><p>Warm Regards,<br/>[Business_Name]</p>'
  },
  {
    id: 'welcome_customer',
    name: 'Welcome New Customer',
    subject: 'Welcome to [Business_Name]',
    body: '<p>Hi [Customer_Name],</p><p>Welcome to our family. We are thrilled to have you onboard.</p><p>Best,<br/>[Business_Name]</p>'
  }
];

const ComposeMail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const draftId = searchParams.get('draft');

  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [subject, setSubject] = useState('');
  const [bodyHTML, setBodyHTML] = useState('');
  
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [isAiLoading, setIsAiLoading] = useState(false);
  
  const [tone, setTone] = useState('formal'); // formal, friendly, concise
  const [scheduledAt, setScheduledAt] = useState('');
  const [showSchedule, setShowSchedule] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // History for undo (stores last 5 AI generations)
  const [history, setHistory] = useState([]);
  const MAX_HISTORY = 5;

  // Saved templates from DB
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateCategory, setTemplateCategory] = useState('custom');

  useEffect(() => {
    if (draftId) fetchDraft();
    fetchSavedTemplates();
  }, [draftId]);

  const fetchDraft = async () => {
    try {
      const res = await mailApi.getMailDetails(draftId);
      if (res.data.success) {
        const mail = res.data.data;
        setTo(mail.to.join(', '));
        setCc(mail.cc.join(', '));
        setBcc(mail.bcc.join(', '));
        setSubject(mail.subject);
        setBodyHTML(mail.bodyHTML);
      }
    } catch (error) {
      toast.error('Failed to load draft');
    }
  };

  const fetchSavedTemplates = async () => {
    try {
      const res = await mailApi.getTemplates();
      if (res.data.success) {
        setSavedTemplates(res.data.data);
      }
    } catch (error) {
      // silently fail – templates are optional
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setFiles([...files, ...newFiles]);
    }
  };

  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const buildFormData = () => {
    const formData = new FormData();
    if (draftId) formData.append('draftId', draftId);
    formData.append('to', to);
    formData.append('cc', cc);
    formData.append('bcc', bcc);
    formData.append('subject', subject);
    formData.append('bodyHTML', bodyHTML);
    if (scheduledAt) formData.append('scheduledAt', new Date(scheduledAt).toISOString());
    files.forEach(file => {
      formData.append('attachments', file);
    });
    return formData;
  };

  const handleSend = async () => {
    if (!to) return toast.error('Please enter at least one recipient');
    setLoading(true);
    try {
      const formData = buildFormData();
      await mailApi.sendMail(formData);
      toast.success(scheduledAt ? 'Email scheduled successfully!' : 'Email sent successfully!');
      navigate('/mailbox');
    } catch (error) {
      toast.error('Failed to send email');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!to && !subject && !bodyHTML) return navigate('/mailbox');
    setSavingDraft(true);
    try {
      const formData = buildFormData();
      if (draftId) {
        await mailApi.updateDraft(draftId, formData);
        toast.success('Draft updated');
      } else {
        await mailApi.createDraft(formData);
        toast.success('Saved to drafts');
      }
      navigate('/mailbox');
    } catch (error) {
      toast.error('Failed to save draft');
    } finally {
      setSavingDraft(false);
    }
  };

  const applyTemplate = (template) => {
    setSubject(template.subject);
    setBodyHTML(template.body);
    toast.success(`Template "${template.name}" applied`);
  };

  const handleDeleteTemplate = async (id) => {
    try {
      await mailApi.deleteTemplate(id);
      setSavedTemplates(savedTemplates.filter(t => t._id !== id));
      toast.success('Template deleted');
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  // Push current state to history before AI overwrites
  const pushToHistory = () => {
    setHistory(prev => {
      const updated = [...prev, { subject, bodyHTML }];
      return updated.slice(-MAX_HISTORY); // keep last N
    });
  };

  // Undo last AI generation
  const handleUndo = () => {
    if (history.length === 0) return toast.info('No previous version to undo');
    const last = history[history.length - 1];
    setSubject(last.subject);
    setBodyHTML(last.bodyHTML);
    setHistory(history.slice(0, -1));
    toast.success('Reverted to previous version');
  };

  const handleAiAction = async (action) => {
    if (!bodyHTML && !subject) return toast.error('Write something first!');
    
    // Save current state before AI overwrites
    pushToHistory();
    
    setIsAiLoading(true);
    try {
      // Pass the plain text of bodyHTML and subject for AI to process
      let strippedBody = bodyHTML ? bodyHTML.replace(/<[^>]+>/g, '').trim() : '';
      let inputText = strippedBody;
      if (!strippedBody && subject) {
        inputText = subject; // Fallback to subject if body is empty
      } else if (subject) {
        inputText = `Subject: ${subject}\n\nBody: ${strippedBody}`;
      }
      
      const res = await mailApi.aiAssist(action, inputText, JSON.stringify({ tone }));
      if (res.data.success) {
        try {
          // AI might return markdown formatting like ```json ... ```, so we clean it
          let rawData = res.data.data;
          rawData = rawData.replace(/```json/g, '').replace(/```/g, '').trim();
          
          const parsedData = JSON.parse(rawData);
          
          if (parsedData.subject) {
             setSubject(parsedData.subject);
          }
          if (parsedData.body) {
             // Replace line breaks if AI returned plain text instead of HTML
             let formattedHtml = parsedData.body;
             if (!formattedHtml.includes('<p>') && !formattedHtml.includes('<br>')) {
               formattedHtml = formattedHtml.replace(/\n\n/g, '</p><p>').replace(/\n/g, '<br/>');
               formattedHtml = `<p>${formattedHtml}</p>`;
             }
             setBodyHTML(formattedHtml);
          }
          toast.success('AI magic applied!');
        } catch (parseError) {
          console.error("Failed to parse AI response:", parseError, res.data.data);
          toast.error("AI generated invalid format. Please try again.");
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'AI Assist failed');
    } finally {
      setIsAiLoading(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) return toast.error('Please enter a template name');
    if (!subject && !bodyHTML) return toast.error('Nothing to save – compose something first');
    try {
      await mailApi.saveTemplate({
        name: templateName.trim(),
        subject,
        body: bodyHTML,
        category: templateCategory
      });
      toast.success('Template saved!');
      setShowSaveTemplateModal(false);
      setTemplateName('');
      fetchSavedTemplates(); // refresh list
    } catch (error) {
      toast.error('Failed to save template');
    }
  };

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{'list': 'ordered'}, {'list': 'bullet'}],
      ['link', 'clean']
    ]
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 h-[calc(100vh-100px)] flex overflow-hidden">
      
      {/* Main Composer Area */}
      <div className="flex-1 flex flex-col min-w-0 border-r border-gray-200 dark:border-gray-700">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gray-50 dark:bg-gray-800">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">New Message</h2>
          <div className="flex items-center gap-2">
            {/* Undo Button */}
            {history.length > 0 && (
              <button 
                onClick={handleUndo}
                className="flex items-center gap-1 text-[13px] font-bold text-orange-500 hover:text-orange-700 px-3 py-2 rounded-lg hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors"
                title={`Undo AI (${history.length} versions)`}
              >
                <ArrowUturnLeftIcon className="h-4 w-4" />
                Undo ({history.length})
              </button>
            )}
            {/* Preview Toggle */}
            <button 
              onClick={() => setShowPreview(!showPreview)}
              className={`flex items-center gap-1 text-[13px] font-bold px-3 py-2 rounded-lg transition-colors ${
                showPreview 
                  ? 'text-blue-600 bg-blue-50 dark:bg-blue-900/20' 
                  : 'text-gray-500 hover:text-gray-900 dark:hover:text-white'
              }`}
              title="Toggle Preview"
            >
              {showPreview ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
              {showPreview ? 'Hide Preview' : 'Preview'}
            </button>
            <button 
              onClick={handleSaveDraft}
              disabled={savingDraft || loading}
              className="text-[14px] font-bold text-gray-500 hover:text-gray-900 dark:hover:text-white px-3 py-2 rounded-lg transition-colors"
            >
              {savingDraft ? 'Saving...' : 'Save Draft'}
            </button>
            <button 
              onClick={() => navigate('/mailbox')}
              className="p-2 text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Form Fields */}
        <div className="flex-1 overflow-y-auto p-6 flex flex-col">
          <div className="space-y-4 mb-6">
            <div className="flex items-center border-b border-gray-200 dark:border-gray-700 pb-2">
              <label className="text-[14px] font-bold text-gray-500 w-16">To</label>
              <input 
                type="text" 
                className="flex-1 bg-transparent border-none focus:ring-0 text-[15px] font-medium text-gray-900 dark:text-white" 
                placeholder="customer@email.com"
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </div>
            <div className="flex items-center border-b border-gray-200 dark:border-gray-700 pb-2">
              <label className="text-[14px] font-bold text-gray-500 w-16">Cc</label>
              <input 
                type="text" 
                className="flex-1 bg-transparent border-none focus:ring-0 text-[15px] font-medium text-gray-900 dark:text-white" 
                value={cc}
                onChange={(e) => setCc(e.target.value)}
              />
            </div>
            <div className="flex items-center border-b border-gray-200 dark:border-gray-700 pb-2">
              <label className="text-[14px] font-bold text-gray-500 w-16">Subject</label>
              <input 
                type="text" 
                className="flex-1 bg-transparent border-none focus:ring-0 text-[15px] font-bold text-gray-900 dark:text-white" 
                placeholder="Enter subject here..."
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>
          </div>

          {/* Rich Text Editor */}
          <div className={`flex-1 flex ${showPreview ? 'gap-4' : ''} min-h-[300px]`}>
            <div className={`${showPreview ? 'w-1/2' : 'w-full'} flex flex-col`}>
              <ReactQuill 
                theme="snow" 
                value={bodyHTML} 
                onChange={setBodyHTML}
                modules={modules}
                className="flex-1 flex flex-col text-gray-900 dark:text-white"
                placeholder="Write your message here..."
              />
            </div>

            {/* Inline Preview Pane */}
            {showPreview && (
              <div className="w-1/2 border border-gray-200 dark:border-gray-700 rounded-xl overflow-y-auto bg-white dark:bg-gray-950">
                <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                  <p className="text-[12px] font-bold text-gray-500 uppercase tracking-wider">📧 Email Preview</p>
                </div>
                <div className="p-4">
                  {subject && (
                    <div className="mb-3 pb-3 border-b border-gray-100 dark:border-gray-800">
                      <p className="text-[11px] font-bold text-gray-400 uppercase mb-1">Subject</p>
                      <p className="text-[15px] font-bold text-gray-900 dark:text-white">{subject}</p>
                    </div>
                  )}
                  <div 
                    className="prose prose-sm dark:prose-invert max-w-none text-[14px] text-gray-700 dark:text-gray-300"
                    dangerouslySetInnerHTML={{ __html: bodyHTML || '<p class="text-gray-400 italic">Start typing to see preview...</p>' }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Attachments Preview */}
          {files.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex flex-wrap gap-3">
                {files.map((file, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-gray-100 dark:bg-gray-800 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700">
                    <DocumentIcon className="h-5 w-5 text-gray-500" />
                    <span className="text-[13px] font-medium text-gray-700 dark:text-gray-300 max-w-[150px] truncate">{file.name}</span>
                    <button onClick={() => removeFile(idx)} className="ml-1 text-gray-400 hover:text-red-500">
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Schedule Picker Bar */}
        {showSchedule && (
          <div className="px-4 py-3 bg-blue-50 dark:bg-blue-900/20 border-t border-blue-100 dark:border-blue-800 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ClockIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              <span className="text-[14px] font-bold text-blue-800 dark:text-blue-300">Schedule Email</span>
              <input 
                type="datetime-local" 
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
                className="ml-2 text-sm border border-blue-200 rounded-lg p-1 text-gray-700 dark:text-white dark:bg-gray-800"
              />
            </div>
            <button onClick={() => {setShowSchedule(false); setScheduledAt('');}} className="text-blue-600 hover:text-blue-800">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        )}

        {/* Bottom Bar */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center rounded-xl overflow-hidden shadow-sm">
              <button
                onClick={handleSend}
                disabled={loading}
                className="bg-[#16a34a] hover:bg-[#15803d] text-white px-6 py-3 font-bold text-[15px] flex items-center gap-2 transition-colors disabled:opacity-70"
              >
                {loading ? 'Sending...' : (scheduledAt ? 'Schedule Send' : 'Send')}
                <PaperAirplaneIcon className="h-5 w-5" />
              </button>
              <button 
                onClick={() => setShowSchedule(!showSchedule)}
                className="bg-[#15803d] hover:bg-[#16a34a] text-white px-3 py-3 border-l border-green-700 transition-colors"
                title="Schedule Send"
              >
                <ClockIcon className="h-5 w-5" />
              </button>
            </div>
            
            <label className="cursor-pointer p-2 text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full transition-colors flex items-center justify-center">
              <PaperClipIcon className="h-6 w-6" />
              <input type="file" multiple className="hidden" onChange={handleFileChange} />
            </label>
          </div>
          
          <button 
            onClick={handleSaveDraft}
            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full transition-colors"
            title="Discard"
          >
            <TrashIcon className="h-6 w-6" />
          </button>
        </div>
      </div>

      {/* Right Sidebar - AI & Templates */}
      <div className="w-80 bg-gray-50 dark:bg-gray-800 flex flex-col hidden lg:flex">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
          <SparklesIcon className="h-5 w-5 text-purple-600" />
          <h3 className="font-bold text-gray-900 dark:text-white">Smart Tools</h3>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          
          {/* Tone Selector */}
          <div>
            <h4 className="text-[12px] font-bold text-gray-500 uppercase tracking-wider mb-2">Tone</h4>
            <div className="flex gap-2">
              {['formal', 'friendly', 'concise'].map(t => (
                <button
                  key={t}
                  onClick={() => setTone(t)}
                  className={`flex-1 px-2 py-1.5 rounded-lg text-[12px] font-bold capitalize transition-all ${
                    tone === t
                      ? 'bg-purple-600 text-white shadow-md'
                      : 'bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-purple-400'
                  }`}
                >
                  {t === 'formal' ? '👔' : t === 'friendly' ? '😊' : '⚡'} {t}
                </button>
              ))}
            </div>
          </div>

          {/* AI Tools */}
          <div>
            <h4 className="text-[12px] font-bold text-gray-500 uppercase tracking-wider mb-3">AI Assist</h4>
            <div className="space-y-2">
              <button onClick={() => handleAiAction('rewrite_professional')} disabled={isAiLoading} className="w-full text-left px-3 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-[13px] font-medium text-gray-700 dark:text-gray-300 hover:border-purple-400 hover:text-purple-600 transition-colors disabled:opacity-50">
                👔 Rewrite as Professional
              </button>
              <button onClick={() => handleAiAction('rewrite_friendly')} disabled={isAiLoading} className="w-full text-left px-3 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-[13px] font-medium text-gray-700 dark:text-gray-300 hover:border-purple-400 hover:text-purple-600 transition-colors disabled:opacity-50">
                😊 Rewrite as Friendly
              </button>
              <button onClick={() => handleAiAction('translate_hindi')} disabled={isAiLoading} className="w-full text-left px-3 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-[13px] font-medium text-gray-700 dark:text-gray-300 hover:border-purple-400 hover:text-purple-600 transition-colors disabled:opacity-50">
                🌐 Translate to Hindi
              </button>
              <button onClick={() => handleAiAction('subject_generator')} disabled={isAiLoading} className="w-full text-left px-3 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-[13px] font-medium text-gray-700 dark:text-gray-300 hover:border-purple-400 hover:text-purple-600 transition-colors disabled:opacity-50">
                ✍️ Generate Subject Line
              </button>
            </div>
            {isAiLoading && (
              <div className="mt-3 flex items-center gap-2 text-purple-600">
                <div className="h-4 w-4 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
                <span className="text-[12px] font-bold">AI is thinking...</span>
              </div>
            )}
          </div>

          {/* Save as Template Button */}
          <div>
            <button 
              onClick={() => setShowSaveTemplateModal(true)}
              disabled={!subject && !bodyHTML}
              className="w-full flex items-center justify-center gap-2 px-3 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg text-[13px] font-bold hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-md"
            >
              <BookmarkIcon className="h-4 w-4" />
              Save as Template
            </button>
          </div>

          {/* Default Templates */}
          <div>
            <h4 className="text-[12px] font-bold text-gray-500 uppercase tracking-wider mb-3">Built-in Templates</h4>
            <div className="space-y-2">
              {defaultTemplates.map(template => (
                <div 
                  key={template.id} 
                  onClick={() => applyTemplate(template)}
                  className="p-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl cursor-pointer hover:border-blue-400 transition-colors group"
                >
                  <p className="text-[13px] font-bold text-gray-900 dark:text-white group-hover:text-blue-600 transition-colors">{template.name}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Saved Templates from DB */}
          {savedTemplates.length > 0 && (
            <div>
              <h4 className="text-[12px] font-bold text-gray-500 uppercase tracking-wider mb-3">My Templates</h4>
              <div className="space-y-2">
                {savedTemplates.map(template => (
                  <div 
                    key={template._id} 
                    className="p-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl cursor-pointer hover:border-green-400 transition-colors group flex items-center justify-between"
                  >
                    <div onClick={() => applyTemplate(template)} className="flex-1">
                      <p className="text-[13px] font-bold text-gray-900 dark:text-white group-hover:text-green-600 transition-colors">{template.name}</p>
                      <p className="text-[11px] text-gray-400 capitalize">{template.category}</p>
                    </div>
                    <button 
                      onClick={(e) => { e.stopPropagation(); handleDeleteTemplate(template._id); }}
                      className="p-1 text-gray-400 hover:text-red-500 rounded transition-colors"
                      title="Delete template"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Save Template Modal */}
      {showSaveTemplateModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-6 w-[400px] max-w-[90vw]">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Save as Template</h3>
            <div className="space-y-4">
              <div>
                <label className="text-[13px] font-bold text-gray-600 dark:text-gray-400 mb-1 block">Template Name *</label>
                <input 
                  type="text"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="e.g. Payment Follow-up"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-[14px]"
                  autoFocus
                />
              </div>
              <div>
                <label className="text-[13px] font-bold text-gray-600 dark:text-gray-400 mb-1 block">Category</label>
                <select 
                  value={templateCategory}
                  onChange={(e) => setTemplateCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-[14px]"
                >
                  <option value="custom">Custom</option>
                  <option value="payments">Payments</option>
                  <option value="greetings">Greetings</option>
                  <option value="follow_up">Follow-up</option>
                </select>
              </div>
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                <p className="text-[11px] font-bold text-gray-500 uppercase mb-1">Preview</p>
                <p className="text-[13px] font-bold text-gray-700 dark:text-gray-300">{subject || 'No subject'}</p>
                <p className="text-[12px] text-gray-500 mt-1 line-clamp-2" dangerouslySetInnerHTML={{ __html: bodyHTML || 'No body' }} />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button 
                onClick={() => { setShowSaveTemplateModal(false); setTemplateName(''); }}
                className="px-4 py-2 text-[14px] font-bold text-gray-500 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={handleSaveTemplate}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-[14px] font-bold rounded-lg transition-colors"
              >
                Save Template
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ComposeMail;
